import { type FC } from 'react';
import { useUnit } from 'effector-react';
import { ScrollArea, Stack } from '@mantine/core';
import { useNavigate } from 'react-router';
import { getOrderPath } from '~/entities/order';
import { usePayinOrdersList, type PayinOrder, type PayinOrdersFilters } from '~/entities/payin';
import { type PaginationModel } from '~/shared/lib/pagination';
import { Pagination } from '~/shared/ui/Pagination/Pagination';
import { OrdersTable } from './components';
import { REFETCH_INTERVAL } from './constants';

interface OrdersListProps {
    paginationModel: PaginationModel;
    filters: PayinOrdersFilters;
    isPollingEnabled?: boolean;
}

export const OrdersList: FC<OrdersListProps> = ({ filters, paginationModel, isPollingEnabled = false }) => {
    const navigate = useNavigate();
    const pagination = useUnit(paginationModel.$pagination);

    const { data: { items, page: paginationMeta } = {}, isLoading } = usePayinOrdersList(
        {
            'filter.requisiteSuffixes': filters.requisiteSuffixes,
            'filter.currency': filters.currency,
            'filter.statuses': filters.statuses,
            'filter.publicStatuses': filters.publicStatuses,
            'filter.traderName.value': filters.traderName?.value,
            'filter.createdAtGt': filters.createdAtGt,
            'filter.createdAtLt': filters.createdAtLt,
            'filter.orderId.value': filters.orderId?.value,
            'filter.amountGt.value': filters.amountGt?.value,
            'filter.amountLt.value': filters.amountLt?.value,
            'pagination.page': pagination.page.toString(),
            'pagination.perPage': pagination.perPage.toString(),
        },
        isPollingEnabled ? REFETCH_INTERVAL : undefined,
    );

    const total = Number(paginationMeta?.total);
    const totalPages = Math.ceil(total / pagination.perPage);

    const handleRowClick = (item: PayinOrder) => {
        const orderId = item.id?.value;

        if (!orderId) {
            return;
        }

        navigate(getOrderPath('payin', orderId));
    };

    return (
        <Stack>
            <ScrollArea h={512}>
                <OrdersTable
                    orders={items}
                    onRowClick={handleRowClick}
                    loading={isLoading}
                />
            </ScrollArea>

            {totalPages > 1 && (
                <Pagination
                    model={paginationModel}
                    usePerPage
                    total={totalPages}
                />
            )}
        </Stack>
    );
};
